import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import {
  selectOptionCurrency,
  selectOptionLegsProperties,
  selectOptionLegsValidation,
} from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';
import { changeLinkedCurrency } from '@/store/option/thunks/thunk.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

type Props = {
  tileId: string;
  legId: LegId;
  className?: string;
};

export function Notional({ tileId, legId, className }: Props) {
  const dispatch = useAppDispatch();
  const [checkOption] = useCheckOption();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { notional, currencyIndex, optionCurrency, validation } = useOptionSelector((tile) => {
    const optionCurrency = selectOptionCurrency(tile);
    const legProperties = selectOptionLegsProperties(tile, legId);
    const validation = selectOptionLegsValidation(tile, legId)?.notional;

    return {
      notional: legProperties?.notional,
      currencyIndex: legProperties?.notionalCurrencyIndex,
      optionCurrency,
      validation,
    };
  });

  const notionalCurrency = currencyIndex === 2 ? optionCurrency.currency2 : optionCurrency.currency1;

  function handleAmountChange(notional: number | undefined) {
    sgwtWebAnalytics?.trackEvent('Option', 'Notional changed', String(notional));
    checkOption({
      tileId,
      legProperties: {
        legId,
        notional,
        notionalCurrencyIndex: currencyIndex,
      },
    });
  }
  function handleAmountCurrencyChange() {
    const newNotionalCurrencyIndex: CurrencyIndex = currencyIndex === 1 ? 2 : 1;
    dispatch(changeLinkedCurrency({ type: 'changeValue', tileId, legId, currency: newNotionalCurrencyIndex }));
    sgwtWebAnalytics?.trackEvent(
      'Option',
      'Notional currency changed',
      newNotionalCurrencyIndex === 1 ? 'Currency1' : 'Currency2',
    );
  }

  return (
    <TradeCaptureValidationWrapper validation={validation}>
      <SgAmountInput
        size="sm"
        onUnitClick={handleAmountCurrencyChange}
        unit={notionalCurrency}
        value={notional}
        data-e2e="notional"
        onChange={handleAmountChange}
        unitBackgroundColor={currencyIndex === 2 ? 'warning' : undefined}
        className={className}
        placeholder="Notional"
      />
    </TradeCaptureValidationWrapper>
  );
}
